type Obj = Record<string, unknown>;

const isObject = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);

/** Drops unset (`undefined`) list entries; an empty list is unset. */
function clean(v: unknown): unknown {
  if (!Array.isArray(v)) {
    return v;
  }
  const list = v.filter((x) => x !== undefined);
  return list.length > 0 ? list : undefined;
}

function flatten(v: unknown, prefix: string, out: Obj): Obj {
  if (isObject(v) && (prefix === "" || Object.keys(v).length > 0)) {
    for (const [k, x] of Object.entries(v)) {
      flatten(x, prefix ? `${prefix}.${k}` : k, out);
    }
  } else if (prefix !== "") {
    out[prefix] = v;
  }
  return out;
}

/** JSON with sorted object keys, so equal values compare equal regardless of key order. */
export function canonical(v: unknown): string {
  return JSON.stringify(v, (_, x: unknown) =>
    isObject(x) ? Object.fromEntries(Object.entries(x).sort(([a], [b]) => a.localeCompare(b))) : x,
  );
}

/** JSON Merge Patch (RFC 7386, top level) turning `before` into `after`; null when nothing changed. */
export function mergePatch(before: Obj, after: Obj): Obj | null {
  const patch: Obj = {};
  for (const k of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const v = clean(after[k]);
    if (v === undefined) {
      if (k in before) {
        patch[k] = null;
      }
    } else if (canonical(v) !== canonical(before[k])) {
      patch[k] = v;
    }
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

interface DiffRow {
  path: string;
  before: unknown;
  after: unknown;
}

/** Changed leaf paths between two object images (`null` = absent object). */
export function diff(before: unknown, after: unknown): DiffRow[] {
  const a = flatten(before, "", {});
  const b = flatten(after, "", {});
  return [...new Set([...Object.keys(a), ...Object.keys(b)])]
    .sort()
    .filter((k) => canonical(a[k]) !== canonical(b[k]))
    .map((path) => ({ path, before: a[path], after: b[path] }));
}
